import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { PopInProps } from "@companion-core/shared/app/Components/PopIn/popIn.model";
import { UseCancelPopInProps } from "@companion-core/shared/app/Components/Requests/RequestDetail/CancelPopIn/cancelPopIn.model";
import { useCancelRequest } from "@companion-core/shared/app/Queries";

export const useCancelPopIn = ({ requestData, loader, onClose }: UseCancelPopInProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: cancelRequest } = useCancelRequest();
  const [popInArgs, setPopInArgs] = useState<PopInProps | null>(null);

  const sendCancelRequest = useCallback(async () => {
    setPopInArgs({
      testID: "request-detail__cancel-popin",
      content: loader,
      freeze: true,
    });
    try {
      await cancelRequest({ id: requestData.id, family: requestData.family ?? "" });
      await queryClient.invalidateQueries({ queryKey: ["request"] });
      await queryClient.invalidateQueries({ queryKey: ["my-requests"] });
      setPopInArgs({
        testID: "request-detail__cancel-popin",
        icon: "JP-Circle-check",
        title: t("request.my-requests.detail.popin.cancel-done.title"),
        content: t("request.my-requests.detail.popin.cancel-done.text"),
        primaryButton: {
          text: t("request.my-requests.detail.popin.cancel.close-button.text"),
          onClick: () => onClose(),
          onPress: () => onClose(),
        },
        freeze: false,
      });
    } catch {
      setPopInArgs({
        testID: "request-detail__cancel-popin",
        icon: "JP-Circle-close",
        title: t("request.my-requests.detail.popin.cancel-error.title"),
        content: t("request.my-requests.detail.popin.cancel-error.text"),
        primaryButton: {
          text: t("request.my-requests.detail.popin.cancel.close-button.text"),
          onClick: () => onClose(),
          onPress: () => onClose(),
        },
        freeze: false,
      });
    }
  }, [requestData, onClose, queryClient]);
  const popInInitialArgs: PopInProps = useMemo(
    () => ({
      testID: "request-detail__cancel-popin",
      icon: "JP-Circle-close",
      title: t("request.my-requests.detail.popin.cancel.title"),
      content: `${t("request.my-requests.detail.popin.cancel.text.pre")}${requestData.subject}${requestData.location ? ((t("request.my-requests.location.particle") + requestData.location.city) as string) : ""}${t("request.my-requests.detail.popin.cancel.text.post")}`,
      primaryButton: {
        text: t("request.my-requests.detail.popin.cancel.primary-button.text"),
        onClick: async () => await sendCancelRequest(),
        onPress: async () => await sendCancelRequest(),
      },
      secondaryButton: {
        text: t("request.my-requests.detail.popin.cancel.secondary-button.text"),
        onClick: () => onClose(),
        onPress: () => onClose(),
      },
      onClose: () => onClose(),
      freeze: false,
    }),
    [requestData, onClose, sendCancelRequest]
  );

  useEffect(() => {
    setPopInArgs(popInInitialArgs);
  }, []);

  return {
    popInArgs,
  };
};
